import React, { memo, useState } from 'react';
import {
    Play, Check, Trash2, Edit3, MessageSquare, CornerDownRight, Save, X
} from 'lucide-react';
import { formatTime } from '../utils/helpers';

function FeedbackItem({
    feedback,
    isActive,
    onSeek,
    onResolve,
    onDelete,
    onEdit, // function(id, newText)
    onReply, // function(id, replyText)
}) {
    const [isEditing, setIsEditing] = useState(false);
    const [editText, setEditText] = useState(feedback.text);

    const [isReplying, setIsReplying] = useState(false);
    const [replyText, setReplyText] = useState(feedback.reply || '');

    const startEdit = (e) => {
        e.stopPropagation();
        setIsEditing(true);
        setEditText(feedback.text);
    };

    const cancelEdit = () => {
        setIsEditing(false);
        setEditText(feedback.text);
    };

    const saveEdit = () => {
        if (editText.trim()) {
            onEdit(feedback.id, editText);
            setIsEditing(false);
        }
    };

    const startReply = (e) => {
        e.stopPropagation();
        setIsReplying(true);
        setReplyText(feedback.reply || '');
    };

    const cancelReply = () => {
        setIsReplying(false);
        setReplyText(feedback.reply || '');
    };

    const saveReply = () => {
        onReply(feedback.id, replyText);
        setIsReplying(false);
    };

    return (
        <div
            onClick={() => onSeek(feedback)}
            className={`
        relative p-4 rounded-xl border transition-all duration-200 cursor-pointer group
        ${isActive
                    ? 'bg-cyan-500/10 border-cyan-500/50 shadow-[0_0_20px_rgba(0,168,232,0.1)]'
                    : 'bg-[#1a1a1a]/80 border-white/5 hover:bg-[#202020] hover:border-white/10'
                }
        ${feedback.isResolved ? 'opacity-60' : 'opacity-100'}
      `}
        >
            {/* Header */}
            <div className="flex items-start justify-between gap-3 mb-2">
                <div className="flex items-center gap-2">
                    <span className="flex items-center gap-1.5 px-2 py-1 rounded bg-[#00a8e8] text-white text-xs font-bold font-mono">
                        <Play size={10} fill="currentColor" />
                        {formatTime(feedback.timestamp)}
                    </span>
                    <span className="font-semibold text-gray-300 text-sm">
                        {feedback.authorName}
                    </span>
                </div>

                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                        onClick={(e) => { e.stopPropagation(); onResolve(feedback.id); }}
                        className={`p-1.5 rounded-lg transition-colors ${feedback.isResolved ? 'bg-green-500/20 text-green-400' : 'hover:bg-white/10 text-gray-400 hover:text-green-400'}`}
                        title={feedback.isResolved ? "Mark as unresolved" : "Mark as resolved"}
                    >
                        <Check size={14} />
                    </button>

                    <button
                        onClick={startEdit}
                        className="p-1.5 rounded-lg hover:bg-white/10 text-gray-400 hover:text-cyan-400 transition-colors"
                    >
                        <Edit3 size={14} />
                    </button>

                    <button
                        onClick={(e) => { e.stopPropagation(); onDelete(feedback.id); }}
                        className="p-1.5 rounded-lg hover:bg-red-500/10 text-gray-400 hover:text-red-400 transition-colors"
                    >
                        <Trash2 size={14} />
                    </button>
                </div>
            </div>

            {/* Content */}
            {isEditing ? (
                <div className="mb-2" onClick={e => e.stopPropagation()}>
                    <textarea
                        value={editText}
                        onChange={(e) => setEditText(e.target.value)}
                        className="w-full bg-black/40 border border-cyan-500/30 rounded-lg p-2 text-sm text-white focus:outline-none focus:border-cyan-500 transition-colors resize-none"
                        rows={2}
                        autoFocus
                    />
                    <div className="flex justify-end gap-2 mt-2">
                        <button onClick={cancelEdit} className="p-1 text-gray-400 hover:text-white"><X size={14} /></button>
                        <button onClick={saveEdit} className="p-1 text-cyan-400 hover:text-cyan-300"><Save size={14} /></button>
                    </div>
                </div>
            ) : (
                <p className={`text-sm leading-relaxed mb-3 ${feedback.isResolved ? 'text-gray-500 line-through' : 'text-gray-200'}`}>
                    {feedback.text}
                </p>
            )}

            {/* Attached Screenshot */}
            {feedback.screenshot && !isEditing && (
                <div className="mb-3 mt-1">
                    <img
                        src={feedback.screenshot}
                        alt="Annotation"
                        className="rounded-lg border border-white/10 max-h-32 object-contain bg-black/50"
                        onClick={(e) => {
                            e.stopPropagation();
                            window.open(feedback.screenshot, '_blank');
                        }}
                    />
                </div>
            )}

            {/* Reply Section */}
            {(feedback.reply || isReplying) ? (
                <div className="mt-3 pl-3 border-l-2 border-white/10" onClick={e => e.stopPropagation()}>
                    {isReplying ? (
                        <div className="flex flex-col gap-2">
                            <textarea
                                value={replyText}
                                onChange={(e) => setReplyText(e.target.value)}
                                placeholder="Write a reply..."
                                className="w-full bg-black/20 border border-white/10 rounded-lg p-2 text-sm text-gray-300 focus:outline-none focus:border-gray-500 transition-colors resize-none"
                                rows={2}
                                autoFocus
                            />
                            <div className="flex justify-end gap-2">
                                <button onClick={cancelReply} className="text-xs text-gray-500 hover:text-gray-300">Cancel</button>
                                <button onClick={saveReply} className="text-xs text-cyan-400 hover:text-cyan-300 font-medium">Post Reply</button>
                            </div>
                        </div>
                    ) : (
                        <div className="group/reply relative">
                            <div className="flex items-center gap-2 mb-1">
                                <CornerDownRight size={12} className="text-gray-500" />
                                <span className="text-xs font-semibold text-gray-400">Reply</span>
                            </div>
                            <p className="text-sm text-gray-400">{feedback.reply}</p>
                            <button
                                onClick={startReply}
                                className="absolute top-0 right-0 p-1 opacity-0 group-hover/reply:opacity-100 hover:text-cyan-400 text-gray-500 transition-all"
                            >
                                <Edit3 size={12} />
                            </button>
                        </div>
                    )}
                </div>
            ) : (
                <button
                    onClick={startReply}
                    className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-gray-300 transition-colors mt-2"
                >
                    <MessageSquare size={12} />
                    Reply
                </button>
            )}
        </div>
    );
}

export default memo(FeedbackItem);
